import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Users, 
  Store, 
  AlertCircle, 
  UserCheck, 
  PackageCheck,
  TrendingUp,
  TrendingDown,
  UserPlus,
  BadgeCheck,
  Mail as MailIcon
} from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from "@/components/ui/card";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area 
} from "recharts";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent 
} from "@/components/ui/chart";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";

export const Route = createFileRoute("/admin/")({
  component: AdminOverview,
});

function AdminOverview() {
  const navigate = useNavigate();

  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => {
      const [
        { count: pendingUsers },
        { count: pendingSuppliers },
        { count: approvedUsers },
        { count: publishedSuppliers },
        { count: openReports },
        { count: nonClients },
        { count: pendingClaims },
        { count: newMessages },
        { count: noCnpjCount }
      ] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }).eq("status", "pendente"),
        supabase.from("fornecedores").select("*", { count: "exact", head: true }).eq("status", "pendente"),
        supabase.from("profiles").select("*", { count: "exact", head: true }).eq("status", "aprovado"),
        supabase.from("fornecedores").select("*", { count: "exact", head: true }).eq("status", "aprovado"),
        supabase.from("denuncias").select("*", { count: "exact", head: true }).eq("status", "aberta"),
        supabase.from("profiles").select("*", { count: "exact", head: true }).eq("status", "nao_cliente"),
        supabase.from("reivindicacoes").select("*", { count: "exact", head: true }).eq("status", "pendente"),
        (supabase.from("contatos" as any) as any).select("*", { count: "exact", head: true }).eq("status", "novo"),
        supabase.from("fornecedores").select("*", { count: "exact", head: true }).or('cnpj.is.null,cnpj.eq.""')
      ]);

      return {
        pendingUsers: pendingUsers || 0,
        pendingSuppliers: pendingSuppliers || 0,
        approvedUsers: approvedUsers || 0,
        publishedSuppliers: publishedSuppliers || 0,
        openReports: openReports || 0,
        nonClients: nonClients || 0,
        pendingClaims: pendingClaims || 0,
        newMessages: newMessages || 0,
        noCnpjCount: noCnpjCount || 0,
      };
    },
  });

  const { data: chartData, isLoading: loadingChart } = useQuery({
    queryKey: ["admin-chart-data"],
    queryFn: async () => {
      const last30Days = Array.from({ length: 30 }, (_, i) => {
        const date = subDays(new Date(), i);
        return {
          date: format(date, "yyyy-MM-dd"),
          display: format(date, "dd/MM", { locale: ptBR }),
          count: 0
        };
      }).reverse();

      const { data: users } = await supabase
        .from("profiles")
        .select("created_at")
        .gte("created_at", subDays(new Date(), 30).toISOString());

      if (users) {
        users.forEach(u => {
          const dateStr = format(new Date(u.created_at!), "yyyy-MM-dd");
          const day = last30Days.find(d => d.date === dateStr);
          if (day) day.count++;
        });
      }

      return last30Days;
    },
  });

  const stats = [
    {
      title: "Usuários Pendentes",
      value: metrics?.pendingUsers || 0,
      icon: Users,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      href: "/admin/usuarios?status=pendente"
    },
    {
      title: "Fornecedores Pendentes",
      value: metrics?.pendingSuppliers || 0,
      icon: Store,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      href: "/admin/fornecedores?status=pendente"
    },
    {
      title: "Usuários Aprovados",
      value: metrics?.approvedUsers || 0,
      icon: UserCheck,
      color: "text-green-600",
      bgColor: "bg-green-50",
      href: "/admin/usuarios?status=aprovado"
    },
    {
      title: "Fornecedores Ativos",
      value: metrics?.publishedSuppliers || 0,
      icon: PackageCheck,
      color: "text-primary-foreground",
      bgColor: "bg-primary/20",
      href: "/admin/fornecedores?status=aprovado"
    },
    {
      title: "Denúncias Abertas",
      value: metrics?.openReports || 0,
      icon: AlertCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
      href: "/admin/denuncias"
    },
    {
      title: "Leads (Não-clientes)",
      value: metrics?.nonClients || 0,
      icon: UserPlus,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      href: "/admin/usuarios?status=nao_cliente"
    },
    {
      title: "Reivindicações Pendentes",
      value: metrics?.pendingClaims || 0,
      icon: BadgeCheck,
      color: "text-brand-dark",
      bgColor: "bg-primary/20",
      href: "/admin/reivindicacoes"
    },
    {
      title: "Mensagens novas",
      value: metrics?.newMessages || 0,
      icon: MailIcon,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      href: "/admin/contatos"
    },
    {
      title: "Sem CNPJ",
      value: metrics?.noCnpjCount || 0,
      icon: AlertCircle,
      color: "text-gray-500",
      bgColor: "bg-gray-50",
      href: "/admin/fornecedores?tab=no_cnpj"
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Card 
            key={stat.title} 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate({ to: stat.href })}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <div className={cn("p-2 rounded-lg", stat.bgColor)}>
                <stat.icon className={cn("w-4 h-4", stat.color)} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loadingMetrics ? "..." : stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="col-span-4">
        <CardHeader>
          <CardTitle>Novos Cadastros</CardTitle>
          <CardDescription>
            Crescimento de usuários nos últimos 30 dias
          </CardDescription>
        </CardHeader>
        <CardContent className="h-[300px]">
          <ChartContainer config={{
            count: {
              label: "Cadastros",
              color: "hsl(var(--primary))",
            },
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData || []}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C6F764" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#C6F764" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis 
                  dataKey="display" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  minTickGap={30}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip content={<ChartTooltipContent />} />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#0C1A12" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorCount)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(" ");
}
